// ===== Shared Particle Animation Logic =====
// Used by both CanvasPreview and ElementPicker

import type { AnimationConfig } from '../types';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  baseVx?: number;  // Original baseline velocity for restoration after cursor effect
  baseVy?: number;
  size: number;
  color: string;
  opacity?: number;
  rotation?: number;
  rotationSpeed?: number;
  // Depth layers
  depth?: number; // 0-1, глубина слоя
  // Rain-specific
  length?: number;
  trail?: { x: number; y: number }[];
  // Stars-specific
  flickerOffset?: number;
}

// Default config values for easy extension
const DEFAULTS = {
  count: 100,
  speed: 1,
  size: 3,
  opacity: 1,
  color: '#ffffff',
  direction: 'down',
  wind: 0,
  gravity: 0,
  waveEnabled: false,
  waveAmplitude: 10,
  waveFrequency: 0.02,
  waveSpeed: 1,
  snowflakeStyle: 'flake',
  colorPalette: 'default',
  depthEnabled: true,
  depthLayers: 3,
  depthFocus: 0.5,
  depthBlur: false,
  cursorEffect: 'none',
  cursorRadius: 100,
  cursorStrength: 1,
  cursorInertia: 0.95,
  scaleCount: true
} as const;

// Color palettes for specific effects
const COLOR_PALETTES: Record<string, string[]> = {
  fire: ['#ff4500', '#ff6600', '#ff8800', '#ffaa00', '#ffcc00', '#ff0000'],
  ocean: ['#006994', '#0077be', '#00bfff', '#40e0d0', '#20b2aa'],
  forest: ['#228b22', '#006400', '#32cd32', '#90ee90', '#8fbc8f'],
  neon: ['#ff00ff', '#00ffff', '#ff0080', '#8000ff', '#00ff80'],
  rainbow: ['#ff0000', '#ff7700', '#ffff00', '#00ff00', '#0000ff', '#8b00ff'],
  sunset: ['#ff6b6b', '#feca57', '#ff9ff3', '#54a0ff', '#5f27cd'],
  default: ['#ffffff']
} as const;

// Helper to get colors for a subType
const getColorsForSubType = (subType: string, configColor?: string | string[], colorPalette?: string): string[] => {
  // Если выбрана палитра - используем её (приоритет)
  if (colorPalette && colorPalette !== 'default') {
    return COLOR_PALETTES[colorPalette] || COLOR_PALETTES.default;
  }
  
  // Иначе используем свой цвет, если есть
  if (configColor && Array.isArray(configColor)) return configColor;
  if (configColor && typeof configColor === 'string') return [configColor];
  
  return COLOR_PALETTES.default;
};

type ConfigWithDefaults = AnimationConfig & typeof DEFAULTS;

const getConfig = (config: Record<string, unknown>): ConfigWithDefaults => ({
  ...DEFAULTS,
  ...config
} as ConfigWithDefaults);

// Helper to assign depth to particles based on config
const assignDepth = (cfg: ConfigWithDefaults): number => {
  if (!cfg.depthEnabled || (cfg.depthLayers || 1) <= 1) return 0.5;
  
  const layers = cfg.depthLayers || 3;
  const focus = cfg.depthFocus ?? 0.5;
  const layer = Math.floor(Math.random() * layers);
  const normalized = layer / (layers - 1);
  
  // Distance from focus point - particles closer to focus are more common
  const dist = Math.abs(normalized - focus);
  const weight = 1 - dist;
  
  // Use weighted random to favor particles near focus
  if (Math.random() < weight * 0.7 + 0.3) {
    return normalized;
  }
  return Math.random();
};

export const createParticles = (
  canvas: HTMLCanvasElement,
  config: Record<string, unknown>,
  subType: string,
  scaleToSize?: { width: number; height: number }
): Particle[] => {
  const cfg = getConfig(config);
  
  // Calculate scaled count based on target size if enabled
  let particleCount: number = cfg.count ?? 100;
  const shouldScale = (cfg.scaleCount as boolean | undefined) !== false; // default true
  if (shouldScale && scaleToSize) {
    const referenceArea = canvas.width * canvas.height;
    const targetArea = scaleToSize.width * scaleToSize.height;
    const scaleFactor = Math.sqrt(targetArea / referenceArea);
    particleCount = Math.max(10, Math.floor((cfg.count ?? 100) * scaleFactor));
  }
  
  // Default palette for specific subTypes - используется если не задана конкретная палитра
  let defaultPalette: string | undefined;
  const userPalette = cfg.colorPalette as string | undefined;
  
  // Если пользователь не выбрал палитру (default или undefined) - используем специфичную для типа
  if (!userPalette || userPalette === 'default') {
    if (subType === 'fire') defaultPalette = 'fire';
    else if (subType === 'rain') defaultPalette = 'ocean';
  } else {
    defaultPalette = userPalette;
  }
  
  const colors = getColorsForSubType(subType, cfg.color, defaultPalette);
  const particles: Particle[] = [];

  for (let i = 0; i < particleCount; i++) {
    const depth = assignDepth(cfg);
    const baseVx = cfg.speed * (Math.random() - 0.5);
    const baseVy = cfg.speed * (Math.random() - 0.5) * 2;
    
    const baseParticle: Particle = {
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: baseVx,
      vy: baseVy,
      baseVx,
      baseVy,
      size: cfg.size,
      color: colors[Math.floor(Math.random() * colors.length)],
      opacity: cfg.opacity,
      depth
    };

    switch (subType) {
      case 'snow':
        particles.push({
          ...baseParticle,
          vy: cfg.speed * (0.5 + Math.random() * 0.5),
          size: cfg.size * (0.5 + Math.random() * 1),
          opacity: cfg.opacity * (0.3 + Math.random() * 0.7),
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.02
        });
        break;
      case 'rain':
        particles.push({
          ...baseParticle,
          vy: cfg.speed * (3 + Math.random() * 2),
          size: cfg.size * 0.2,
          vx: cfg.speed * 0.05,
          length: 10 + Math.random() * 12,
          opacity: cfg.opacity * (0.5 + Math.random() * 0.3),
          trail: [] as { x: number; y: number }[]
        });
        break;
      case 'stars':
        particles.push({
          ...baseParticle,
          // Статичные звезды с мерцанием и лёгким дрейфом
          vx: (Math.random() - 0.5) * 0.1,
          vy: (Math.random() - 0.5) * 0.1,
          size: cfg.size * (0.3 + Math.random() * 0.7),
          // Для мерцания используем позицию
          flickerOffset: Math.random() * Math.PI * 2
        });
        break;
      case 'fire':
        particles.push({
          ...baseParticle,
          // Огонь начинается снизу
          y: canvas.height + Math.random() * 30,
          vy: -cfg.speed * (1.5 + Math.random() * 1),
          vx: (Math.random() - 0.5) * cfg.speed * 0.4,
          size: cfg.size * (1 + Math.random() * 2),
          opacity: cfg.opacity * (0.6 + Math.random() * 0.4)
        });
        break;
      case 'bubbles':
        particles.push({
          ...baseParticle,
          vy: -cfg.speed * (0.5 + Math.random() * 0.5),
          size: cfg.size * (1 + Math.random() * 2),
          opacity: cfg.opacity * (0.3 + Math.random() * 0.4)
        });
        break;
      case 'confetti':
        particles.push({
          ...baseParticle,
          vy: cfg.speed * 2,
          vx: (Math.random() - 0.5) * cfg.speed * 2,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.2,
          size: cfg.size * (0.5 + Math.random())
        });
        break;
      default:
        particles.push(baseParticle);
    }
  }

  return particles;
};

export interface CursorPosition {
  x: number;
  y: number;
}

export const updateParticle = (
  p: Particle,
  canvas: HTMLCanvasElement,
  config: Record<string, unknown>,
  subType: string,
  cursorPos?: CursorPosition | null
) => {
  const cfg = getConfig(config);

  // Parallax effect - closer layers move slightly faster
  const depthFactor = cfg.depthEnabled && p.depth !== undefined 
    ? (1 - p.depth) * 0.3 + 0.7  // Softer: 0.7-1.0 instead of 0.5-1.0
    : 1;

  // Cursor effect
  let cursorMultiplier = 1;
  if (cursorPos && cfg.cursorEffect && cfg.cursorEffect !== 'none') {
    const radius = cfg.cursorRadius ?? 100;
    const strength = cfg.cursorStrength ?? 1;
    const dx = p.x - cursorPos.x;
    const dy = p.y - cursorPos.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (dist < radius) {
      const factor = 1 - (dist / radius); // 0 at edge, 1 at center
      
      switch (cfg.cursorEffect) {
        case 'fear':
          // Частицы убегают от курсора с лёгким отталкиванием друг от друга
          p.vx += (dx / dist) * strength * factor * 3;
          p.vy += (dy / dist) * strength * factor * 3;
          // Добавляем небольшое случайное отклонение для предотвращения точного重叠
          p.vx += (Math.random() - 0.5) * factor * 0.5;
          p.vy += (Math.random() - 0.5) * factor * 0.5;
          break;
          
        case 'attract':
          // Частицы притягиваются к курсору с учётом минимального расстояния
          const minDist = p.size * 3; // Минимальное расстояние между частицами
          if (dist > minDist) {
            p.vx -= (dx / dist) * strength * factor * 2;
            p.vy -= (dy / dist) * strength * factor * 2;
          } else {
            // Если слишком близко - отталкиваем обратно
            p.vx += (dx / dist) * strength * factor * 0.5;
            p.vy += (dy / dist) * strength * factor * 0.5;
          }
          break;
          
        case 'stop':
          // Остановка анимации
          p.vx *= (1 - factor * 0.8);
          p.vy *= (1 - factor * 0.8);
          break;
          
        case 'avoid':
          // Плавное огибание - случайное направление (по или против часовой)
          const direction = Math.random() > 0.5 ? 1 : -1;
          const angle = Math.atan2(dy, dx) + (Math.PI / 2) * direction;
          const tangentForce = strength * factor * 0.8;
          p.vx += Math.cos(angle) * tangentForce + (Math.random() - 0.5) * factor * 0.3;
          p.vy += Math.sin(angle) * tangentForce + (Math.random() - 0.5) * factor * 0.3;
          break;
          
        case 'fade':
          // Исчезновение (фейд) частиц в радиусе курсора
          p.opacity = (p.opacity ?? 1) * (1 - factor * 0.95);
          // Если почти невидимы - пересоздаём в случайном месте
          if (p.opacity < 0.05) {
            p.x = Math.random() * canvas.width;
            p.y = Math.random() * canvas.height;
            p.opacity = cfg.opacity;
          }
          break;
          
        case 'slow':
          // Замедление
          cursorMultiplier = 1 - factor * 0.7;
          break;
      }
    }
  }
  
  // Apply velocity dampening to reduce endless movement (only when NOT in cursor radius)
  const inertia = cfg.cursorInertia ?? 0.95;
  const isInRadius = cursorPos && cfg.cursorEffect !== 'none' && cfg.cursorRadius 
    ? Math.sqrt(Math.pow(p.x - cursorPos.x, 2) + Math.pow(p.y - cursorPos.y, 2)) < cfg.cursorRadius 
    : false;
  
  // Only apply dampening and restoration when cursor is NOT affecting the particle
  if (!isInRadius && cfg.cursorEffect !== 'none' && cfg.cursorEffect !== 'stop' && cfg.cursorEffect !== 'slow' && cfg.cursorEffect !== 'fade') {
    // Gradual velocity restoration towards baseline movement (stored baseVx/baseVy)
    p.vx = p.vx * inertia + (p.baseVx || 0) * (1 - inertia);
    p.vy = p.vy * inertia + (p.baseVy || 0) * (1 - inertia);
  }

  p.x += (p.vx + cfg.wind) * depthFactor * cursorMultiplier;
  p.y += (p.vy + cfg.gravity) * depthFactor * cursorMultiplier;

  const dirMultiplier = subType === 'bubbles' ? 0 : 1;
  const direction = cfg.direction as string;
  
  if (direction === 'down') p.y += cfg.speed * dirMultiplier;
  if (direction === 'up') p.y -= cfg.speed * dirMultiplier;
  if (direction === 'right') p.x += cfg.speed * dirMultiplier;
  if (direction === 'left') p.x -= cfg.speed * dirMultiplier;

  if (cfg.waveEnabled && subType !== 'stars') {
    p.x += Math.sin(p.y * cfg.waveFrequency + Date.now() * 0.001 * cfg.waveSpeed) * cfg.waveAmplitude * 0.1;
  }

  switch (subType) {
    case 'snow':
      if (p.rotation !== undefined && p.rotationSpeed !== undefined) {
        p.rotation += p.rotationSpeed;
      }
      if (p.y > canvas.height + 10) {
        p.y = -10;
        p.x = Math.random() * canvas.width;
        p.rotation = Math.random() * Math.PI * 2;
      }
      break;
    case 'rain':
      // Добавляем позицию в след (motion trail)
      if (p.trail) {
        p.trail.unshift({ x: p.x, y: p.y });
        if (p.trail.length > 6) p.trail.pop();
      }
      
      if (p.y > canvas.height + 30) {
        p.y = -(p.length ?? 20) - Math.random() * 20;
        p.x = Math.random() * canvas.width;
        p.trail = [];
      }
      break;
    case 'stars':
      // Мерцание звезд на основе времени
      const flickerBase = p.flickerOffset ?? 0;
      p.opacity = 0.2 + Math.abs(Math.sin(Date.now() * 0.003 + flickerBase)) * 0.8;
      
      // Лёгкий дрейф
      p.x += p.vx;
      p.y += p.vy;
      
      // Мягкое перемещение при выходе за границу
      if (p.x < 0) p.x = canvas.width;
      if (p.x > canvas.width) p.x = 0;
      if (p.y < 0) p.y = canvas.height;
      if (p.y > canvas.height) p.y = 0;
      break;
    case 'fire':
      p.size *= 0.99;
      // Огонь поднимается вверх, колеблется влево-вправо
      p.vx += (Math.random() - 0.5) * 0.15;
      p.vx *= 0.97; // затухание
      
      if (p.y < -50 || p.size < 0.5) {
        // Сброс вниз с небольшим интервалом для реалистичности
        p.y = canvas.height + 10 + Math.random() * 20;
        p.x = Math.random() * canvas.width;
        p.size = cfg.size * (1 + Math.random() * 2);
        p.vx = (Math.random() - 0.5) * cfg.speed * 0.4;
        p.opacity = cfg.opacity * (0.6 + Math.random() * 0.4);
      }
      break;
    case 'bubbles':
      if (p.y < -p.size - 10) {
        p.y = canvas.height + p.size + 10;
        p.x = Math.random() * canvas.width;
      }
      break;
    case 'confetti':
      if (p.rotation !== undefined && p.rotationSpeed !== undefined) {
        p.rotation += p.rotationSpeed;
      }
      if (p.y > canvas.height + 20) {
        p.y = -20;
        p.x = Math.random() * canvas.width;
        p.vx = (Math.random() - 0.5) * cfg.speed * 2;
      }
      break;
  }

  if (p.x < -20) p.x = canvas.width + 20;
  if (p.x > canvas.width + 20) p.x = -20;
};

export const drawBackgroundParticle = (
  ctx: CanvasRenderingContext2D,
  p: Particle,
  subType: string,
  config: Record<string, unknown>,
  canvas?: HTMLCanvasElement
) => {
  const cfg = getConfig(config);
  
  // Apply depth-based rendering effects
  let depthOpacity = p.opacity ?? 1;
  let depthSize = p.size;
  
  if (cfg.depthEnabled && p.depth !== undefined) {
    // Blur for distant layers (optional - performance heavy)
    if (cfg.depthBlur && p.depth > 0.7) {
      const blur = (p.depth - 0.7) * 3;
      ctx.filter = `blur(${blur}px)`;
    }
    
    // Softer opacity - distant layers only slightly more transparent
    depthOpacity = (p.opacity ?? 1) * (0.7 + (1 - p.depth) * 0.3);
    
    // Subtle size difference
    depthSize = p.size * (0.7 + p.depth * 0.4);
  }
  
  ctx.fillStyle = p.color;
  ctx.globalAlpha = depthOpacity;

  const snowflakeStyle = cfg.snowflakeStyle;

  if (subType === 'confetti') {
    ctx.save();
    ctx.translate(p.x, p.y);
    if (p.rotation !== undefined) ctx.rotate(p.rotation);
    ctx.fillStyle = p.color;
    ctx.fillRect(-depthSize / 2, -depthSize / 2, depthSize, depthSize * 0.6);
    ctx.restore();
  } else if (subType === 'rain') {
    const length = p.length || 20;
    const angle = Math.atan2(p.vy, p.vx);
    const endX = p.x - Math.cos(angle) * length;
    const endY = p.y - Math.sin(angle) * length;
    
    // Рисуем градиентный след (motion blur)
    const gradient = ctx.createLinearGradient(p.x, p.y, endX, endY);
    gradient.addColorStop(0, p.color);
    gradient.addColorStop(0.7, p.color + 'aa');
    gradient.addColorStop(1, 'transparent');
    
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(endX, endY);
    ctx.strokeStyle = gradient;
    ctx.lineWidth = depthSize;
    ctx.lineCap = 'round';
    ctx.stroke();
    
    // Рисуем мягкое пятно на конце (разбрызгивание)
    if (canvas && p.y > canvas.height - 10) {
      ctx.globalAlpha = (p.opacity || 0.5) * 0.3;
      ctx.beginPath();
      ctx.arc(p.x, canvas!.height, p.size, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.fill();
    }
  } else if (subType === 'bubbles') {
    ctx.beginPath();
    ctx.arc(p.x, p.y, depthSize, 0, Math.PI * 2);
    ctx.strokeStyle = p.color;
    ctx.lineWidth = 0.5;
    ctx.stroke();
    
    ctx.globalAlpha = 0.08;
    ctx.fillStyle = p.color;
    ctx.fill();
    
    const highlightX = p.x - depthSize * 0.3;
    const highlightY = p.y - depthSize * 0.3;
    ctx.beginPath();
    ctx.arc(highlightX, highlightY, depthSize * 0.25, 0, Math.PI * 2);
    ctx.globalAlpha = 0.4;
    ctx.fillStyle = '#fff';
    ctx.fill();
    
    ctx.globalAlpha = 1;
  } else if (subType === 'snow') {
    const styleStr = snowflakeStyle as string;
    const actualStyle = styleStr === 'mixed' 
      ? (['flake', 'star', 'circle'] as const)[Math.floor(Math.random() * 3)]
      : snowflakeStyle;
    
    if (actualStyle === 'circle') {
      ctx.beginPath();
      ctx.arc(p.x, p.y, depthSize * 0.5, 0, Math.PI * 2);
      ctx.fill();
    } else if (actualStyle === 'star') {
      ctx.save();
      ctx.translate(p.x, p.y);
      if (p.rotation !== undefined) ctx.rotate(p.rotation);
      
      ctx.beginPath();
      for (let i = 0; i < 12; i++) {
        const r = i % 2 === 0 ? depthSize : depthSize * 0.4;
        const angle = (Math.PI * 2 / 12) * i;
        if (i === 0) ctx.moveTo(Math.cos(angle) * r, Math.sin(angle) * r);
        else ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
      }
      ctx.closePath();
      ctx.fillStyle = p.color;
      ctx.fill();
      ctx.restore();
    } else {
      // Snowflake (6 branches)
      ctx.save();
      ctx.translate(p.x, p.y);
      if (p.rotation !== undefined) ctx.rotate(p.rotation);
      
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 / 6) * i;
        ctx.rotate(angle);
        
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(0, -depthSize);
        ctx.strokeStyle = p.color;
        ctx.lineWidth = depthSize * 0.2;
        ctx.stroke();
        
        if (depthSize > 2) {
          ctx.beginPath();
          ctx.moveTo(0, -depthSize * 0.5);
          ctx.lineTo(depthSize * 0.3, -depthSize * 0.8);
          ctx.moveTo(0, -depthSize * 0.5);
          ctx.lineTo(-depthSize * 0.3, -depthSize * 0.8);
          ctx.lineWidth = depthSize * 0.15;
          ctx.stroke();
        }
        
        ctx.rotate(-angle);
      }
      
      ctx.restore();
    }
  } else if (subType === 'stars') {
    ctx.beginPath();
    ctx.arc(p.x, p.y, depthSize * 0.5, 0, Math.PI * 2);
    ctx.fill();
  } else if (subType === 'fire') {
    ctx.beginPath();
    ctx.arc(p.x, p.y, depthSize, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.arc(p.x, p.y, depthSize, 0, Math.PI * 2);
    ctx.fill();
  }

  // Reset effects
  ctx.globalAlpha = 1;
  ctx.filter = 'none';
};